import { supabase } from '../config/supabaseClient';

/**
 * Audit Logging Service
 * Tracks authentication and security events for compliance and monitoring
 *
 * Event Types:
 * - LOGIN_SUCCESS: User successfully logged in
 * - LOGIN_FAILED: Failed login attempt
 * - LOGOUT: User logged out
 * - SESSION_EXPIRED: Session expired due to timeout
 * - PASSWORD_RESET_REQUEST: User requested password reset
 * - PASSWORD_CHANGED: User changed password
 * - ACCOUNT_CREATED: New account created
 * - ACCOUNT_DELETED: Account deleted
 * - TOKEN_REFRESH: JWT token refreshed
 * - UNAUTHORIZED_ACCESS: Attempted access to protected route without auth
 */

const EVENT_TYPES = {
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILED: 'LOGIN_FAILED',
  LOGOUT: 'LOGOUT',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  PASSWORD_RESET_REQUEST: 'PASSWORD_RESET_REQUEST',
  PASSWORD_CHANGED: 'PASSWORD_CHANGED',
  ACCOUNT_CREATED: 'ACCOUNT_CREATED',
  ACCOUNT_DELETED: 'ACCOUNT_DELETED',
  TOKEN_REFRESH: 'TOKEN_REFRESH',
  UNAUTHORIZED_ACCESS: 'UNAUTHORIZED_ACCESS',
  OAUTH_LOGIN: 'OAUTH_LOGIN'
};

/**
 * Get browser and device information
 */
const getDeviceInfo = () => {
  const userAgent = navigator.userAgent;
  const platform = navigator.platform;
  const language = navigator.language;

  return {
    user_agent: userAgent,
    platform: platform,
    language: language,
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    viewport: `${window.innerWidth}x${window.innerHeight}`
  };
};

/**
 * Get IP address (requires external service or backend)
 * For now, we'll use a placeholder
 */
const getIPAddress = async () => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error('Failed to get IP address:', error);
    return 'unknown';
  }
};

/**
 * Log an audit event
 * @param {string} eventType - Type of event (from EVENT_TYPES)
 * @param {string} userId - User ID (optional, null for failed logins)
 * @param {object} metadata - Additional event metadata
 */
export const logAuditEvent = async (eventType, userId = null, metadata = {}) => {
  try {
    const deviceInfo = getDeviceInfo();
    const ipAddress = await getIPAddress();

    const auditLog = {
      event_type: eventType,
      user_id: userId,
      ip_address: ipAddress,
      user_agent: deviceInfo.user_agent,
      platform: deviceInfo.platform,
      metadata: {
        ...metadata,
        language: deviceInfo.language,
        screen_resolution: deviceInfo.screen_resolution,
        viewport: deviceInfo.viewport,
        timestamp: new Date().toISOString(),
        url: window.location.href
      }
    };

    // Log to console for debugging
    console.log(`[AUDIT LOG] ${eventType}:`, auditLog);

    // Store in localStorage as fallback (last 100 events)
    const localAuditLogs = JSON.parse(localStorage.getItem('auditLogs') || '[]');
    localAuditLogs.unshift(auditLog);
    if (localAuditLogs.length > 100) {
      localAuditLogs.pop();
    }
    localStorage.setItem('auditLogs', JSON.stringify(localAuditLogs));

    // TODO: Store in Supabase database table 'audit_logs'
    // Uncomment when audit_logs table is created:
    /*
    const { error } = await supabase
      .from('audit_logs')
      .insert([auditLog]);

    if (error) {
      console.error('Failed to store audit log in database:', error);
    }
    */

    return { success: true, log: auditLog };
  } catch (error) {
    console.error('Error logging audit event:', error);
    return { success: false, error };
  }
};

/**
 * Log successful login
 */
export const logLoginSuccess = async (userId, email, method = 'email') => {
  return await logAuditEvent(EVENT_TYPES.LOGIN_SUCCESS, userId, {
    email,
    method,
    message: `User ${email} logged in successfully via ${method}`
  });
};

/**
 * Log failed login attempt
 */
export const logLoginFailed = async (email, reason = 'Invalid credentials') => {
  return await logAuditEvent(EVENT_TYPES.LOGIN_FAILED, null, {
    email,
    reason,
    message: `Failed login attempt for ${email}: ${reason}`
  });
};

/**
 * Log user logout
 */
export const logLogout = async (userId, email) => {
  return await logAuditEvent(EVENT_TYPES.LOGOUT, userId, {
    email,
    message: `User ${email} logged out`
  });
};

/**
 * Log session expiration
 */
export const logSessionExpired = async (userId, email, reason = 'Inactivity timeout') => {
  return await logAuditEvent(EVENT_TYPES.SESSION_EXPIRED, userId, {
    email,
    reason,
    message: `Session expired for ${email}: ${reason}`
  });
};

/**
 * Log password reset request
 */
export const logPasswordResetRequest = async (email) => {
  return await logAuditEvent(EVENT_TYPES.PASSWORD_RESET_REQUEST, null, {
    email,
    message: `Password reset requested for ${email}`
  });
};

/**
 * Log password change
 */
export const logPasswordChanged = async (userId, email) => {
  return await logAuditEvent(EVENT_TYPES.PASSWORD_CHANGED, userId, {
    email,
    message: `Password changed for ${email}`
  });
};

/**
 * Log account creation
 */
export const logAccountCreated = async (userId, email) => {
  return await logAuditEvent(EVENT_TYPES.ACCOUNT_CREATED, userId, {
    email,
    message: `New account created for ${email}`
  });
};

/**
 * Log account deletion
 */
export const logAccountDeleted = async (userId, email) => {
  return await logAuditEvent(EVENT_TYPES.ACCOUNT_DELETED, userId, {
    email,
    message: `Account deleted for ${email}`
  });
};

/**
 * Log OAuth login
 */
export const logOAuthLogin = async (userId, email, provider) => {
  return await logAuditEvent(EVENT_TYPES.OAUTH_LOGIN, userId, {
    email,
    provider,
    message: `User ${email} logged in via ${provider} OAuth`
  });
};

/**
 * Log unauthorized access attempt
 */
export const logUnauthorizedAccess = async (path) => {
  return await logAuditEvent(EVENT_TYPES.UNAUTHORIZED_ACCESS, null, {
    path,
    message: `Unauthorized access attempt to ${path}`
  });
};

/**
 * Get audit logs from localStorage
 */
export const getLocalAuditLogs = () => {
  try {
    return JSON.parse(localStorage.getItem('auditLogs') || '[]');
  } catch (error) {
    console.error('Error reading audit logs:', error);
    return [];
  }
};

/**
 * Clear local audit logs
 */
export const clearLocalAuditLogs = () => {
  localStorage.removeItem('auditLogs');
};

export { EVENT_TYPES };
